/* Copyright (c) 2020-present CloudIO Inc */

// Copy this file to src/componentregistry/components/NumberColumn folder
// and import helpRenders into NumberColumnManager.ts file
// import { helpRenders } from './NumberColumn_Help';

import { ComponentSample, Item } from 'componentregistry';

const formatPage: {
  root: Item<'Page'>;
  canvasGrid: Item<'CanvasGrid'>;
  numberNumberColumn: Item<'NumberColumn'>;
  selectSelectColumn: Item<'SelectColumn'>;
  testAliasLocalDataStore: Item<'LocalDataStore'>;
  testAliasDataStoreReference: Item<'DataStoreReference'>;
} = {
  canvasGrid: {
    children: ['numberNumberColumn', 'selectSelectColumn'],
    itemId: 'canvasGrid',
    parent: 'testAliasDataStoreReference',
    props: {
      rowHeight: 36,
      updatable: {
        type: 'expression',
        value: '$.testAlias.id % 2 === 0',
      },
      sortable: false,
    },
    type: 'CanvasGrid',
  },
  numberNumberColumn: {
    children: [],
    itemId: 'numberNumberColumn',
    parent: 'canvasGrid',
    props: {
      formatFunction: 'formatCurrency',
      insertable: {
        type: 'boolean',
        value: true,
      },
      label: { type: 'text', value: 'With Currency Format' },
      updatable: {
        type: 'boolean',
        value: true,
      },
      viewAttribute: 'number',
      width: 129,
    },
    type: 'NumberColumn',
  },
  root: {
    children: ['testAliasLocalDataStore', 'testAliasDataStoreReference'],
    itemId: 'root',
    parent: 'root',
    props: {
      padding: {
        all: 0,
      },
      windowTitle: {
        type: 'text',
        value: 'Blank Page',
      },
    },
    type: 'Page',
  },
  selectSelectColumn: {
    children: [],
    itemId: 'selectSelectColumn',
    parent: 'canvasGrid',
    props: {
      formatFunction: 'formatGenderSelect',
      insertable: {
        type: 'boolean',
        value: true,
      },
      label: { type: 'text', value: 'With Icon' },
      optionValues: { type: 'text', value: 'M:Male,F:Female,O:Other' },
      updatable: {
        type: 'boolean',
        value: true,
      },
      viewAttribute: 'gender',
      width: 94,
    },
    type: 'SelectColumn',
  },
  testAliasDataStoreReference: {
    children: ['canvasGrid'],
    itemId: 'testAliasDataStoreReference',
    parent: 'root',
    props: {
      alias: 'testAlias',
    },
    type: 'DataStoreReference',
  },
  testAliasLocalDataStore: {
    children: [],
    itemId: 'testAliasLocalDataStore',
    parent: 'root',
    props: {
      alias: 'testAlias',
      dataFormat: 'ts',
      dataSource: 'test1',
      tsData:
        "const data = Array.from(Array(10000).keys()).map((_, i) => {\n  const rating = Math.ceil(Math.random() * 5);\n  return {\n    id: i,\n    number: Math.round(Math.random() * 10000) / 100,\n    gender: i % 2 ? 'M' : 'F',\n  };\n});\nreturn data;",
    },
    type: 'LocalDataStore',
  },
};

const controller = `import React from 'react';
import { Box } from '@mui/material';
import type { FormatColumnValueFunction, IconValueWithToolTip } from 'cloudio';

const NF = {
  USD: new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
  }),
};

const formatCurrency: FormatColumnValueFunction = ({ viewAttribute, value, rawValue, record, theme }) => {
  if (typeof rawValue === 'number') {
      return NF.USD.format(rawValue);
  }
  return value;
}

const formatGenderSelect: FormatColumnValueFunction = ({ viewAttribute, value, rawValue, record, theme }) => {
  if (rawValue) {
      let icon: IconValueWithToolTip | undefined;
      switch (rawValue) {
          case 'M':
              icon = { iconName: 'user-hard-hat', color: theme.palette.primary.main, iconType: 'light' };
              break;
          default:
              icon = { iconName: 'user', color: 'pink', iconType: 'light', iconSize: 16, tooltip: \`some tooltip... \${value}\` };
              break;
      }
      return { value, icon };
  }
  return value;
}


// must export a default object with all the functions to be used by this page
export default {
  formatGenderSelect,
  formatCurrency,
}`;

export const numberFormatHelpRenders: ComponentSample<'NumberColumn'>[] = [
  {
    title: 'Custom Formating using Controller Function',
    description: `Format Function can be used to format the value of the column. The function is defined in the controller and is passed the value of the column. The function can return a string or an object of type IconValueWithToolTip to render the column value with an icon (with an optional tooltip) prefix.

\`\`\`alert
Create a new page with this example and check the controller code for the format functions
\`\`\`

###### Sample Controller Code
\`\`\`typescript
import type { FormatColumnValueFunction, IconValueWithToolTip } from 'cloudio';

const NF = {
  USD: new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
  }),
};

const formatCurrency: FormatColumnValueFunction = ({ viewAttribute, value, rawValue, record, theme }) => {
  if (typeof rawValue === 'number') {
      return NF.USD.format(rawValue);
  }
  return value;
}

const formatGenderSelect: FormatColumnValueFunction = ({ viewAttribute, value, rawValue, record, theme }) => {
  if (rawValue) {
      let icon: IconValueWithToolTip | undefined;
      switch (rawValue) {
          case 'M':
              icon = { iconName: 'user-hard-hat', color: theme.palette.primary.main, iconType: 'light' };
              break;
          default:
              icon = { iconName: 'user', color: 'pink', iconType: 'light', iconSize: 16, tooltip: \`some tooltip... \${value}\` };
              break;
      }
      return { value, icon };
  }
  return value;
}
\`\`\`
`,
    page: formatPage,
    controller,
    exampleId: 'eg-01GRF26D17RN42YHFRQ26C9DXE',
    clearParentChildren: false,
    height: 280,
    visibleProps: ['formatFunction'],
    item: {
      type: 'NumberColumn',
      props: {
        label: { type: 'text', value: 'With Currency Format' },
        width: 200,
        updatable: {
          type: 'boolean',
          value: true,
        },
        insertable: {
          type: 'boolean',
          value: true,
        },
        viewAttribute: 'number',
        formatFunction: 'formatCurrency',
      },
      itemId: 'numberNumberColumn',
      parent: 'canvasGrid',
      children: [],
    },
  },
];
